// Barre de navigation : bouton retour de l'app, puis en-tête des visiteurs
// (logo, langue, menu hamburger). Données : ReactShell::Navigation.
//
// Les états ouverts (langue, menu) valent pour l'adresse où on les a ouverts :
// la page suivante repart d'un en-tête fermé.
import { useEffect, useRef, useState } from 'react'
import { usePage } from '@inertiajs/react'
import AppLink from '@/components/ui/AppLink'
import { useShared } from '@/hooks/useShared'
import { t } from '@/lib/i18n'

type NavigationLanguage = { label: string; path: string; active: boolean }

type NavigationHeader = {
  homePath: string
  localeLabel: string
  languages: NavigationLanguage[]
  blogPath: string | null
  pricingPath: string
  loginPath: string
  signupPath: string
}

export type NavigationData = {
  backButton: boolean
  header: NavigationHeader | null
}

export default function Navigation({ data }: { data: Record<string, any> }) {
  const { backButton, header } = data as NavigationData
  return (
    <>
      {backButton && (
        <button type="button" className="webview-back-btn webview-back-btn-fixed" onClick={() => window.history.back()} aria-label="Go back">
          <i className="fas fa-chevron-left"></i>
        </button>
      )}
      {header && <NavigationHeaderBar header={header} />}
    </>
  )
}

function NavigationHeaderBar({ header }: { header: NavigationHeader }) {
  const { url } = usePage()
  const { csrfToken } = useShared()
  const [langOpenAt, setLangOpenAt] = useState<string | null>(null)
  const [menuOpenAt, setMenuOpenAt] = useState<string | null>(null)
  const langOpen = langOpenAt === url
  const menuOpen = menuOpenAt === url
  const navRef = useRef<HTMLElement>(null)
  const hamburgerRef = useRef<HTMLButtonElement>(null)

  // Clic hors du menu : il se ferme (pas le choix de langue, qui reste
  // ouvert).
  useEffect(() => {
    if (!menuOpen) return
    const onClick = (e: MouseEvent) => {
      const target = e.target as Node
      if (navRef.current?.contains(target) || hamburgerRef.current?.contains(target)) return
      setMenuOpenAt(null)
    }
    document.addEventListener('click', onClick)
    return () => document.removeEventListener('click', onClick)
  }, [menuOpen])

  return (
    <>
      <header className="nv-header">
        <AppLink className="nv-header-logo" href={header.homePath}>
          <img src="/images/Logo_hor_transp.png" alt="Northvane" className="nv-header-logo-img" />
        </AppLink>

        <div className="nv-header-actions">
          <div className={`nv-lang-dropdown${langOpen ? ' is-open' : ''}`}>
            <button className="nv-lang-btn" onClick={() => setLangOpenAt(langOpen ? null : url)} aria-label="Language">
              {header.localeLabel}{' '}
              <i className="fas fa-chevron-down"></i>
            </button>
            <div className="nv-lang-menu">
              {/* Formulaires natifs : tout le document change de langue. */}
              {header.languages.map((lang) => (
                <form key={lang.path} action={lang.path} acceptCharset="UTF-8" method="post">
                  <input type="hidden" name="authenticity_token" value={csrfToken} autoComplete="off" />
                  <button type="submit" className={`nv-lang-option ${lang.active ? 'active' : ''}`}>{lang.label}</button>
                </form>
              ))}
            </div>
          </div>

          <button
            ref={hamburgerRef}
            className={`nv-hamburger${menuOpen ? ' is-open' : ''}`}
            aria-label="Menu"
            onClick={() => setMenuOpenAt(menuOpen ? null : url)}
          >
            <span className="nv-hamburger-line"></span>
            <span className="nv-hamburger-line"></span>
            <span className="nv-hamburger-line"></span>
          </button>
        </div>
      </header>

      <nav ref={navRef} className={`nv-nav-overlay${menuOpen ? ' is-open' : ''}`} id="nv-nav-overlay">
        {header.blogPath && <AppLink href={header.blogPath}>{t('nav.blog', { default: 'Blog' })}</AppLink>}{' '}
        <AppLink href={header.pricingPath}>{t('footer.pricing', { default: 'Pricing' })}</AppLink>{' '}
        <div className="nv-nav-divider"></div>{' '}
        <AppLink href={header.loginPath}>{t('nav.login', { default: 'Log in' })}</AppLink>{' '}
        <AppLink href={header.signupPath} className="nv-nav-cta">
          {t('nav.signup', { default: 'Sign up' })}
        </AppLink>
      </nav>
    </>
  )
}
